import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  RefreshControl,
  Platform,
  useWindowDimensions,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  RadarChart,
  Radar,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
} from 'recharts';
import { formatPct } from '../../utils/formatPercentage';
import { COLORS, FONTS } from '../../constants/config';
import { PageContainer, Navbar, Breadcrumb } from '../../components';
import OrganizationsComparisonSection from '../../components/OrganizationsComparisonSection';
import { useRoleNavigation } from '../../hooks';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getTextAlign } from '../../utils/rtl';
import { useYear } from '../../contexts/YearContext';
import { organizationsApi } from '../../services/apiService';
import { OrganizationComparison } from '../../types';
import { useAutoTranslate } from '../../hooks/useAutoTranslate';
import { useStrategyComparisonQuery } from '../../hooks/queries';

import { tokens } from '@design';
// Types
interface OrganizationReport extends OrganizationComparison {
  rank: number;
  rating: 'excellent' | 'average' | 'poor';
}

type TabType = 'overview' | 'comparison' | 'charts' | 'ranking';

// eslint-disable-next-line no-restricted-syntax
const BAR_CHART_MARGIN = { top: 20, right: 30, left: 20, bottom: 60 };

// Helper function to get rating based on achievement percentage
// Matches Figma design: ممتاز (>=70%), وسط (50-69%), ضعيف (<50%)
const getRating = (achievement: number): 'excellent' | 'average' | 'poor' => {
  if (achievement >= 70) return 'excellent';
  if (achievement >= 50) return 'average';
  return 'poor';
};

// Helper function to get rating label - uses translation function
const getRatingLabel = (rating: 'excellent' | 'average' | 'poor', t: (key: string) => string): string => {
  switch (rating) {
    case 'excellent': return t('reports.excellent');
    case 'average': return t('reports.average');
    case 'poor': return t('reports.poor');
  }
};

// Helper function to get rating color
const getRatingColor = (rating: 'excellent' | 'average' | 'poor'): string => {
  switch (rating) {
    case 'excellent': return tokens.colors.raw.green["500"];
    case 'average': return '#F59E0B';
    case 'poor': return tokens.colors.raw.red["500"];
  }
};

// Tab Button Component
const TabButton: React.FC<{
  label: string;
  isActive: boolean;
  onPress: () => void;
}> = ({ label, isActive, onPress }) => (
  <TouchableOpacity
    style={[
      styles.tabButton,
      isActive && styles.tabButtonActive,
    ]}
    onPress={onPress}
  >
    <Text style={[styles.tabButtonText, isActive && styles.tabButtonTextActive]}>
      {label}
    </Text>
  </TouchableOpacity>
);

// Filter Dropdown Component - Web only
const FilterDropdown: React.FC<{
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}> = ({ label, value, options, onChange }) => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  // Web-specific styles that can't be in StyleSheet
  const webSelectStyle: React.CSSProperties = {
    backgroundColor: tokens.colors.raw.white,
    border: '1px solid #E5E7EB',
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: '10px 12px 10px 36px',
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: COLORS.neutral700,
    fontFamily: 'JF-Flat-Regular',
    textAlign: getTextAlign(isRTL),
    width: '100%',
    appearance: 'none',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
    backgroundImage: 'none',
    direction: 'rtl',
    cursor: 'pointer',
    outline: 'none',
  };

  return (
    <View style={styles.filterContainer}>
      <select
        style={webSelectStyle}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <View style={styles.filterArrow} pointerEvents="none">
        <Ionicons name="chevron-down" size={16} color={COLORS.neutral500} />
      </View>
    </View>
  );
};

// Progress Ring Component for achievement percentage
const ProgressRing: React.FC<{ percentage: number; size?: number }> = ({ percentage, size = 36 }) => {
  const strokeWidth = 4;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  const getColor = () => {
    if (percentage >= 70) return tokens.colors.raw.green["500"];
    if (percentage >= 50) return '#F59E0B';
    return tokens.colors.raw.red["500"];
  };

  return (
    <View style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={tokens.colors.raw.gray["200"]}
          strokeWidth={strokeWidth}
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={getColor()}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
    </View>
  );
};

// Table Row Component - RTL order: الترتيب | الجهة | عدد المؤشرات | جيد | متوسط | ضعيف | نسبة الإنجاز | المبادرات المكتملة | التقييم | view
const TableRow: React.FC<{
  org: OrganizationReport;
  onPress: () => void;
  translateOrg: (name: string | undefined | null) => string;
  t: (key: string) => string;
}> = ({ org, onPress, translateOrg, t }) => (
  <TouchableOpacity style={styles.tableRow} onPress={onPress}>
    {/* الترتيب - center */}
    <View style={[styles.tableCell, styles.rankCell]}>
      <Text style={styles.rankText}>{org.rank}</Text>
    </View>
    {/* الجهة - right align */}
    <View style={[styles.tableCell, styles.nameCell]}>
      <Text style={styles.orgName}>{translateOrg(org.name)}</Text>
    </View>
    {/* عدد المؤشرات - center */}
    <View style={[styles.tableCell, styles.kpiCountCell]}>
      <Text style={styles.tableCellText}>{org.total_kpis}</Text>
    </View>
    {/* جيد - center */}
    <View style={[styles.tableCell, styles.kpiStatusCell]}>
      <Text style={styles.tableCellText}>{org.kpis_good}</Text>
    </View>
    {/* متوسط - center */}
    <View style={[styles.tableCell, styles.kpiStatusCell]}>
      <Text style={styles.tableCellText}>{org.kpis_average}</Text>
    </View>
    {/* ضعيف - center */}
    <View style={[styles.tableCell, styles.kpiStatusCell]}>
      <Text style={styles.tableCellText}>{org.kpis_poor}</Text>
    </View>
    {/* مؤشر الالتزام - center */}
    <View style={[styles.tableCell, styles.achievementCell]}>
      <View style={styles.achievementCellContent}>
        <ProgressRing percentage={org.commitment_index} size={28} />
        <Text style={styles.achievementText}>{formatPct(org.commitment_index)} %</Text>
      </View>
    </View>
    {/* المبادرات المكتملة - center */}
    <View style={[styles.tableCell, styles.initiativesCell]}>
      <Text style={styles.tableCellText}>{org.initiatives_completed}/{org.total_initiatives}</Text>
    </View>
    {/* التقييم - center */}
    <View style={[styles.tableCell, styles.ratingCell]}>
      <View style={[styles.ratingBadge, { borderColor: getRatingColor(org.rating) }]}>
        <Text style={[styles.ratingText, { color: getRatingColor(org.rating) }]}>
          {getRatingLabel(org.rating, t)}
        </Text>
      </View>
    </View>
    {/* View button */}
    <View style={[styles.tableCell, styles.viewCell]}>
      <TouchableOpacity style={styles.viewButton}>
        <Ionicons name="eye-outline" size={18} color={COLORS.neutral400} />
      </TouchableOpacity>
    </View>
  </TouchableOpacity>
);

// Organization Comparison Card Component for مقارنة تفصيلية tab
const ComparisonCard: React.FC<{
  org: OrganizationReport;
  onPress: () => void;
  translateOrg: (name: string | undefined | null) => string;
  t: (key: string) => string;
}> = ({ org, onPress, translateOrg, t }) => {
  const { colors } = useTheme();
  const cardStyle = { backgroundColor: colors.card, borderColor: colors.cardBorder };
  // Use real data from API
  const totalKpis = org.total_kpis || 0;
  const kpisGood = org.kpis_good || 0;
  const kpisAverage = org.kpis_average || 0;
  const kpisPoor = org.kpis_poor || 0;
  const totalGoals = org.total_goals || 0;
  const goalsAchieved = org.goals_achieved || 0;
  const totalInitiatives = org.total_initiatives || 0;
  const initiativesCompleted = org.initiatives_completed || 0;
  const commitmentIndex = org.commitment_index || 0;

  return (
    <TouchableOpacity style={[styles.comparisonCard, cardStyle]} onPress={onPress}>
      {/* Organization Name */}
      <View style={styles.comparisonCardHeader}>
        <Text style={styles.comparisonCardTitle}>{translateOrg(org.name)}</Text>
        {/* Rating Badge */}
        <View style={[styles.comparisonRatingBadge, { borderColor: getRatingColor(org.rating) }]}>
          <Text style={[styles.comparisonRatingText, { color: getRatingColor(org.rating) }]}>
            {getRatingLabel(org.rating, t)}
          </Text>
        </View>
      </View>

      {/* KPI Stats Grid */}
      <View style={styles.comparisonStatsGrid}>
        {/* Row 1 */}
        <View style={styles.comparisonStatsRow}>
          <View style={[styles.comparisonStatBox, styles.statBoxGreen]}>
            <Text style={styles.comparisonStatLabel}>{t('reports.good')}</Text>
            <Text style={styles.comparisonStatValue}>{kpisGood}</Text>
          </View>
          <View style={[styles.comparisonStatBox, styles.statBoxDefault]}>
            <Text style={styles.comparisonStatLabel}>{t('kpis.title')}</Text>
            <Text style={styles.comparisonStatValue}>{totalKpis}</Text>
          </View>
        </View>

        {/* Row 2 */}
        <View style={styles.comparisonStatsRow}>
          <View style={[styles.comparisonStatBox, styles.statBoxRed]}>
            <Text style={styles.comparisonStatLabel}>{t('reports.poor')}</Text>
            <Text style={styles.comparisonStatValue}>{kpisPoor}</Text>
          </View>
          <View style={[styles.comparisonStatBox, styles.statBoxYellow]}>
            <Text style={styles.comparisonStatLabel}>{t('reports.average')}</Text>
            <Text style={styles.comparisonStatValue}>{kpisAverage}</Text>
          </View>
        </View>
      </View>

      {/* Commitment Index Progress Bar */}
      <View style={styles.comparisonProgressSection}>
        <View style={styles.comparisonProgressHeader}>
          <Text style={[
            styles.comparisonProgressValue,
            { color: commitmentIndex >= 70 ? tokens.colors.raw.green["500"] : commitmentIndex >= 50 ? '#F59E0B' : tokens.colors.raw.red["500"] }
          ]}>
            %{formatPct(commitmentIndex)}
          </Text>
          <Text style={styles.comparisonProgressLabel}>{t('organizations.commitmentIndexLabel')}</Text>
        </View>
        <View style={styles.comparisonProgressBarBg}>
          <View
            style={[
              styles.comparisonProgressBarFill,
              {
                width: `${Math.min(commitmentIndex, 100)}%`,
                backgroundColor: commitmentIndex >= 70 ? tokens.colors.raw.green["500"] : commitmentIndex >= 50 ? '#F59E0B' : tokens.colors.raw.red["500"],
              },
            ]}
          />
        </View>
      </View>

      {/* Bottom Stats */}
      <View style={styles.comparisonBottomStats}>
        <View style={styles.comparisonBottomRow}>
          <Text style={styles.comparisonBottomValue}>{goalsAchieved}/{totalGoals}</Text>
          <Text style={styles.comparisonBottomLabel}>{t('reports.goalsAchieved')}</Text>
        </View>
        <View style={styles.comparisonBottomRow}>
          <Text style={styles.comparisonBottomValue}>{initiativesCompleted}/{totalInitiatives}</Text>
          <Text style={styles.comparisonBottomLabel}>{t('reports.initiativesCompleted')}</Text>
        </View>
      </View>
    </TouchableOpacity>
  );
};

// Custom Tooltip Component for the chart
const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <View style={styles.tooltipContainer}>
        <Text style={styles.tooltipLabel}>{label}</Text>
        {payload.map((entry: any, index: number) => (
          <View key={index} style={styles.tooltipRow}>
            <View style={[styles.tooltipDot, { backgroundColor: entry.color }]} />
            <Text style={styles.tooltipText}>
              {entry.name}: %{entry.value}
            </Text>
          </View>
        ))}
      </View>
    );
  }
  return null;
};

// Custom Legend Component for Arabic labels
const CustomLegend: React.FC<{ t: (key: string) => string }> = ({ t }) => (
  <View style={styles.chartLegend}>
    <View style={styles.legendItem}>
      <View style={[styles.legendDot, { backgroundColor: tokens.colors.raw.green["500"] }]} />
      <Text style={styles.legendText}>{t('reports.excellent')}</Text>
    </View>
    <View style={styles.legendItem}>
      <View style={[styles.legendDot, { backgroundColor: '#F59E0B' }]} />
      <Text style={styles.legendText}>{t('reports.average')}</Text>
    </View>
    <View style={styles.legendItem}>
      <View style={[styles.legendDot, { backgroundColor: tokens.colors.raw.red["500"] }]} />
      <Text style={styles.legendText}>{t('reports.poor')}</Text>
    </View>
  </View>
);

// Multi-line horizontal x-axis tick: wraps long org names across up to 3 lines
// instead of rotating them. Splits on whitespace, ~14 chars per line.
const WrappedXAxisTick: React.FC<any> = ({ x, y, payload }) => {
  const text: string = (payload?.value ?? '') as string;
  const maxCharsPerLine = 14;
  const maxLines = 3;
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let current = '';
  for (const w of words) {
    if (!current) {
      current = w;
    } else if ((current + ' ' + w).length <= maxCharsPerLine) {
      current += ' ' + w;
    } else {
      lines.push(current);
      current = w;
    }
    if (lines.length >= maxLines) break;
  }
  if (current && lines.length < maxLines) lines.push(current);
  if (lines.length === maxLines && words.join(' ').length > lines.join(' ').length) {
    lines[maxLines - 1] = lines[maxLines - 1].slice(0, maxCharsPerLine - 1) + '…';
  }
  return (
    <text
      x={x}
      y={y}
      textAnchor="middle"
      fill={tokens.colors.raw.gray["500"]}
      fontSize={11}
      fontFamily="JF-Flat-Regular"
    >
      {lines.map((line, i) => (
        <tspan key={i} x={x} dy={i === 0 ? 14 : 13}>
          {line}
        </tspan>
      ))}
    </text>
  );
};

// Stacked Bar Chart Component using recharts for رسومات بيانية tab
const StackedBarChart: React.FC<{
  data: OrganizationReport[];
  height?: number;
  translateOrg: (name: string | undefined | null) => string;
  t: (key: string) => string;
}> = ({ data, height = 350, translateOrg, t }) => {
  // Transform data: bar total = commitment_index, sliced by KPI bucket share.
  // Sort desc by commitment_index so the bar order matches the bar heights.
  const chartData = [...data]
    .sort((a, b) => (b.commitment_index || 0) - (a.commitment_index || 0))
    .map((org) => {
      const ci = org.commitment_index || 0;
      const totalKpis = org.total_kpis || 1;
      const goodShare = (org.kpis_good || 0) / totalKpis;
      const averageShare = (org.kpis_average || 0) / totalKpis;
      const poorShare = (org.kpis_poor || 0) / totalKpis;
      const fullName = translateOrg(org.name);
      return {
        name: fullName,
        fullName,
        excellent: Number((goodShare * ci).toFixed(2)),
        average: Number((averageShare * ci).toFixed(2)),
        poor: Number((poorShare * ci).toFixed(2)),
      };
    });

  if (data.length === 0) return null;

  return (
    <View style={styles.chartContainer}>
      <View style={{ width: '100%', height, minWidth: 300, minHeight: height }}>
        <ResponsiveContainer width="100%" height={height} debounce={50}>
          <BarChart
            data={chartData}
            
            margin={BAR_CHART_MARGIN}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={tokens.colors.raw.gray["200"]} />
            <XAxis
              dataKey="name"
              tick={<WrappedXAxisTick />}
              tickLine={false}
              axisLine={false}
              height={64}
              interval={0}
            />
            <YAxis
              tick={{ fontSize: tokens.typography.paragraph["paragraph caption font size"], fill: tokens.colors.raw.gray["500"], fontFamily: FONTS.numbers }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(value) => `%${value}`}
              domain={[0, 100]}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar
              dataKey="poor"
              stackId="a"
              fill={tokens.colors.raw.red["500"]}
              radius={[0, 0, 4, 4]}
              name={t('reports.poor')}
            />
            <Bar
              dataKey="average"
              stackId="a"
              fill="#F59E0B"
              radius={[0, 0, 0, 0]}
              name={t('reports.average')}
            />
            <Bar
              dataKey="excellent"
              stackId="a"
              fill={tokens.colors.raw.green["500"]}
              radius={[4, 4, 0, 0]}
              name={t('reports.excellent')}
            />
          </BarChart>
        </ResponsiveContainer>
      </View>
      <CustomLegend t={t} />
    </View>
  );
};

// Performance Comparison Stacked Bar Chart Component (مقارنة أداء الجهات)
// Shows achieved vs remaining percentage for each organization
const PerformanceComparisonChart: React.FC<{
  data: OrganizationReport[];
  height?: number;
  translateOrg: (name: string | undefined | null) => string;
  t: (key: string) => string;
}> = ({ data, height = 350, translateOrg, t }) => {
  // Transform data for stacked bar chart - achieved vs remaining
  const chartData = [...data]
    .sort((a, b) => b.vision_achievement - a.vision_achievement)
    .map((org) => {
      const achieved = Number(Number(org.vision_achievement).toFixed(2));
      const remaining = 100 - achieved;
      const translatedName = translateOrg(org.name);

      return {
        name: translatedName.length > 15 ? translatedName.substring(0, 15) + '...' : translatedName,
        fullName: translateOrg(org.name),
        achieved: achieved,
        remaining: remaining,
      };
    });

  // Custom legend for stacked chart
  const StackedLegend = () => (
    <View style={styles.chartLegend}>
      <View style={styles.legendItem}>
        <View style={[styles.legendDot, { backgroundColor: tokens.colors.raw.green["500"] }]} />
        <Text style={styles.legendText}>{t('reports.achieved')}</Text>
      </View>
      <View style={styles.legendItem}>
        <View style={[styles.legendDot, { backgroundColor: tokens.colors.raw.gray["200"] }]} />
        <Text style={styles.legendText}>{t('reports.remaining')}</Text>
      </View>
    </View>
  );

  if (data.length === 0) return null;

  return (
    <View style={styles.chartContainer}>
      <View style={{ width: '100%', height, minWidth: 300, minHeight: height }}>
        <ResponsiveContainer width="100%" height={height} debounce={50}>
          <BarChart
            data={chartData}
            
            margin={BAR_CHART_MARGIN}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={tokens.colors.raw.gray["200"]} />
            <XAxis
              dataKey="name"
              tick={{ fontSize: tokens.typography.paragraph["paragraph caption font size"], fill: tokens.colors.raw.gray["500"], fontFamily: 'JF-Flat-Regular' }}
              tickLine={false}
              tickMargin={10}
              axisLine={false}
              angle={-45}
              textAnchor="end"
              height={80}
              interval={0}
            />
            <YAxis
              tick={{ fontSize: tokens.typography.paragraph["paragraph caption font size"], fill: tokens.colors.raw.gray["500"], fontFamily: FONTS.numbers }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(value) => `%${value}`}
              domain={[0, 100]}
            />
            <Tooltip
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  return (
                    <View style={styles.tooltipContainer}>
                      <Text style={styles.tooltipLabel}>{payload[0].payload.fullName}</Text>
                      <View style={styles.tooltipRow}>
                        <View style={[styles.tooltipDot, { backgroundColor: tokens.colors.raw.green["500"] }]} />
                        <Text style={styles.tooltipText}>{t('reports.achieved')}: %{payload[0].value}</Text>
                      </View>
                      <View style={styles.tooltipRow}>
                        <View style={[styles.tooltipDot, { backgroundColor: tokens.colors.raw.gray["200"] }]} />
                        <Text style={styles.tooltipText}>{t('reports.remaining')}: %{payload[1]?.value}</Text>
                      </View>
                    </View>
                  );
                }
                return null;
              }}
            />
            <Bar
              dataKey="achieved"
              stackId="a"
              fill={tokens.colors.raw.green["500"]}
              radius={[0, 0, 4, 4]}
              name={t('charts.achieved')}
            />
            <Bar
              dataKey="remaining"
              stackId="a"
              fill={tokens.colors.raw.gray["200"]}
              radius={[4, 4, 0, 0]}
              name={t('charts.remaining')}
            />
          </BarChart>
        </ResponsiveContainer>
      </View>
      <StackedLegend />
    </View>
  );
};

// Radar chart colors for each metric - matching Figma design
const RADAR_COLORS = {
  strategicGoals: '#46EDD5',      // Cyan - الأهداف الاستراتيجية
  initiatives: '#00A63E',         // Green - المبادرات الاستراتيجية
  processes: '#EC003F',           // Red - العمليات
  services: '#FFB86A',            // Orange - الخدمات
  unitGoals: '#AD46FF',           // Purple - أهداف الوحدات التنظيمية
  humanCapital: '#8EC5FF',        // Light Blue - رأس المال البشري
};

// Radar chart metric labels with Figma colors (labels are translated dynamically in components)
const RADAR_METRICS = [
  { key: 'strategicGoals', labelKey: 'reports.strategicGoals', color: RADAR_COLORS.strategicGoals },
  { key: 'initiatives', labelKey: 'reports.strategicInitiatives', color: RADAR_COLORS.initiatives },
  { key: 'processes', labelKey: 'sidebar.processes', color: RADAR_COLORS.processes },
  { key: 'services', labelKey: 'sidebar.services', color: RADAR_COLORS.services },
  { key: 'unitGoals', labelKey: 'sidebar.unitGoals', color: RADAR_COLORS.unitGoals },
  { key: 'humanCapital', labelKey: 'reports.humanCapital', color: RADAR_COLORS.humanCapital },
];

// Radar fill color (the polygon)
const RADAR_FILL_COLOR = '#EC003F';

// Custom dot component for multi-colored radar points
const RadarCustomDot: React.FC<any> = (props) => {
  const { cx, cy, index } = props;
  const colors = [
    RADAR_COLORS.strategicGoals,  // Cyan
    RADAR_COLORS.initiatives,      // Green
    RADAR_COLORS.processes,        // Red
    RADAR_COLORS.services,         // Orange
    RADAR_COLORS.unitGoals,        // Purple
    RADAR_COLORS.humanCapital,     // Light Blue
  ];
  const color = colors[index] || RADAR_FILL_COLOR;

  return (
    <circle
      cx={cx}
      cy={cy}
      r={5}
      fill={color}
      stroke={tokens.colors.raw.white}
      strokeWidth={2}
    />
  );
};

// Organization Radar Card Component for المقارنة الشاملة متعددة الأبعاد
// Vertical layout: Chart on top, Legend below in 2-column grid (matching Figma)
const OrganizationRadarCard: React.FC<{
  org: OrganizationReport;
  onPress: () => void;
  translateOrg: (name: string | undefined | null) => string;
  t: (key: string) => string;
}> = ({ org, onPress, translateOrg, t }) => {
  const { colors } = useTheme();
  const cardStyle = { backgroundColor: colors.card, borderColor: colors.cardBorder };

  // Translated radar metric labels
  const translatedMetrics = [
    { key: 'strategicGoals', label: t('reports.strategicGoals'), color: RADAR_COLORS.strategicGoals },
    { key: 'initiatives', label: t('reports.strategicInitiatives'), color: RADAR_COLORS.initiatives },
    { key: 'processes', label: t('sidebar.processes'), color: RADAR_COLORS.processes },
    { key: 'services', label: t('sidebar.services'), color: RADAR_COLORS.services },
    { key: 'unitGoals', label: t('sidebar.unitGoals'), color: RADAR_COLORS.unitGoals },
    { key: 'humanCapital', label: t('reports.humanCapital'), color: RADAR_COLORS.humanCapital },
  ];

  // Use real metrics data from API
  const radarData = [
    { category: translatedMetrics[0].label, value: Number(Number(org.strategic_goals_achievement || 0).toFixed(2)), fullMark: 100 },
    { category: translatedMetrics[1].label, value: Number(Number(org.initiatives_achievement || 0).toFixed(2)), fullMark: 100 },
    { category: translatedMetrics[2].label, value: Number(Number(org.processes_achievement || 0).toFixed(2)), fullMark: 100 },
    { category: translatedMetrics[3].label, value: Number(Number(org.services_achievement || 0).toFixed(2)), fullMark: 100 },
    { category: translatedMetrics[4].label, value: Number(Number(org.unit_goals_achievement || 0).toFixed(2)), fullMark: 100 },
    { category: translatedMetrics[5].label, value: Number(Number(org.human_capital_achievement || 0).toFixed(2)), fullMark: 100 },
  ];

  // Legend items with values from data
  const legendItems = translatedMetrics.map((metric, index) => ({
    label: metric.label,
    value: `%${radarData[index].value}`,
    color: metric.color,
  }));

  return (
    <TouchableOpacity style={[styles.radarCard, cardStyle]} onPress={onPress}>
      {/* Organization Name Header */}
      <View style={styles.radarCardHeader}>
        <Text style={styles.radarCardTitle}>{translateOrg(org.name)}</Text>
      </View>

      {/* Vertical layout: Chart on top, Legend below */}
      <View style={styles.radarCardContent}>
        {/* Radar Chart - Centered, with proper domain scaling */}
        <View style={styles.radarChartWrapper}>
          <ResponsiveContainer width="100%" height={140} debounce={50}>
            <RadarChart cx="50%" cy="50%" outerRadius="70%" data={radarData}>
              <PolarGrid stroke={tokens.colors.raw.gray["200"]} />
              <PolarAngleAxis
                dataKey="category"
                tick={false}
              />
              {/* PolarRadiusAxis ensures values scale from 0-100 */}
              <PolarRadiusAxis
                angle={90}
                domain={[0, 100]}
                tick={false}
                axisLine={false}
              />
              <Tooltip
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    return (
                      <View style={styles.radarTooltip}>
                        <Text style={styles.radarTooltipLabel}>{payload[0].payload.category}</Text>
                        <Text style={styles.radarTooltipValue}>%{payload[0].value}</Text>
                      </View>
                    );
                  }
                  return null;
                }}
              />
              <Radar
                name={t('reports.achievementPercentage')}
                dataKey="value"
                stroke={RADAR_FILL_COLOR}
                fill={RADAR_FILL_COLOR}
                fillOpacity={0.3}
                strokeWidth={2}
                dot={<RadarCustomDot />}
              />
            </RadarChart>
          </ResponsiveContainer>
        </View>

        {/* Legend BELOW chart - RTL: [Value] [---] [Label] [●] */}
        <View style={styles.radarLegendContainer}>
          {legendItems.map((item, index) => (
            <View key={index} style={styles.radarLegendRow}>
              <Text style={styles.radarLegendValue} numberOfLines={1}>{item.value}</Text>
              <View style={styles.radarLegendDash} />
              <Text style={styles.radarLegendLabel} numberOfLines={1}>{item.label}</Text>
              <View style={[styles.radarLegendDot, { backgroundColor: item.color }]} />
            </View>
          ))}
        </View>
      </View>
    </TouchableOpacity>
  );
};

// Pagination Component (mirrors SchedulesManagementScreen pagination)
const Pagination: React.FC<{
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  t: (key: string, options?: any) => string;
}> = ({ currentPage, totalPages, totalItems, itemsPerPage, onPageChange, t }) => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const rtlRow = isRTL ? 'row-reverse' as const : 'row' as const;

  if (totalPages <= 0) return null;

  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);
  const paginationText = t('reports.showing', { start: startItem, end: endItem, total: totalItems });

  const getPageNumbers = (): number[] => {
    const pages: number[] = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }
    for (let i = startPage; i <= endPage; i++) pages.push(i);
    return pages;
  };

  return (
    <View style={[styles.paginationRow, { flexDirection: rtlRow }]}>
      {/* Showing X-Y of Z - right side */}
      {totalItems > 0 && (
        <Text style={styles.paginationInfoText}>{paginationText}</Text>
      )}

      {/* Spacer */}
      <View style={{ flex: 1 }} />

      {/* Pagination controls - left side */}
      <View style={[styles.paginationControls, { flexDirection: rtlRow }]}>
        {/* Previous button */}
        <TouchableOpacity
          style={[styles.paginationButton, currentPage === 1 && styles.paginationButtonDisabled]}
          onPress={() => currentPage > 1 && onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          <Text style={styles.paginationButtonText}>{t('reports.previous')}</Text>
          <Ionicons
            name="chevron-forward"
            size={16}
            color={currentPage === 1 ? COLORS.neutral300 : COLORS.neutral600}
          />
        </TouchableOpacity>

        {/* Page numbers */}
        <View style={[styles.paginationNumbers, { flexDirection: rtlRow }]}>
          {getPageNumbers().map((page) => (
            <TouchableOpacity
              key={page}
              style={[
                styles.paginationNumber,
                currentPage === page && styles.paginationNumberActive,
              ]}
              onPress={() => onPageChange(page)}
            >
              <Text
                style={[
                  styles.paginationNumberText,
                  currentPage === page && styles.paginationNumberTextActive,
                ]}
              >
                {page}
              </Text>
            </TouchableOpacity>
          ))}
        </View>

        {/* Next button */}
        <TouchableOpacity
          style={[styles.paginationButton, currentPage === totalPages && styles.paginationButtonDisabled]}
          onPress={() => currentPage < totalPages && onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          <Ionicons
            name="chevron-back"
            size={16}
            color={currentPage === totalPages ? COLORS.neutral300 : COLORS.neutral600}
          />
          <Text style={styles.paginationButtonText}>{t('reports.next')}</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

export default function ExportScreen() {
  const navigation = useNavigation();
  const { canExport } = useRoleNavigation();
  const { width } = useWindowDimensions();
  const { colors, isDark } = useTheme();
  const { t, language } = useLanguage();
  const { selectedYear } = useYear();
  const { translate, translateOrg } = useAutoTranslate();

  // Theme-aware styles for cards
  const themedCardStyles = {
    card: { backgroundColor: colors.card, borderColor: colors.cardBorder },
    cardTitle: { color: colors.textPrimary },
    cardValue: { color: colors.text },
    cardDescription: { color: colors.textMuted },
  };

  // Responsive breakpoints
  const isMobile = width < 768;
  const isTablet = width >= 768 && width < 1024;
  const isDesktop = width >= 1024;

  // State
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  // Comparison data (powers the Reports table + the charts tab). Cached via
  // React Query — re-visiting /export shows the previous result instantly
  // instead of re-fetching and re-rendering a spinner. Year-context
  // invalidates the `['strategy', ...]` family so a year change still
  // triggers a refetch on this screen.
  const {
    data: comparisonRaw,
    isPending: loading,
    isRefetching,
    refetch: refetchComparison,
  } = useStrategyComparisonQuery({
    // Rank + rating follow the commitment index — the metric the Reports
    // table displays (its % column reads org.commitment_index).
    sortBy: 'commitment_index',
    order: 'desc',
    enabled: canExport,
  });
  const [refreshing, setRefreshing] = useState(false);
  const organizations = useMemo<OrganizationReport[]>(() => {
    const list = comparisonRaw ?? [];
    return list.map((org, index) => ({
      ...org,
      rank: index + 1,
      rating: getRating(org.commitment_index),
    }));
  }, [comparisonRaw]);

  // Filters state
  const [selectedOrg, setSelectedOrg] = useState('all');
  const [selectedCriteria, setSelectedCriteria] = useState('all');

  // Sort state for the OrganizationsComparisonSection (مقارنة أداء الجهات)
  // chart in رسومات بيانية. Defaults match the dashboard.
  const [comparisonSortBy, setComparisonSortBy] = useState<string>('commitment_index');
  const [comparisonOrder, setComparisonOrder] = useState<'asc' | 'desc'>('desc');
  const handleComparisonSortChange = useCallback(
    (field: string, nextOrder: 'asc' | 'desc') => {
      setComparisonSortBy(field);
      setComparisonOrder(nextOrder);
    },
    [],
  );

  // Date filter state for charts
  const [fromDate, setFromDate] = useState<Date>(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 6); // 6 months ago
    return date;
  });
  const [toDate, setToDate] = useState<Date>(new Date());

  // Format date for HTML input (YYYY-MM-DD)
  const formatDateForInput = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Redirect unauthorized users (only strategy_team, executive, and superuser can view reports)
  useEffect(() => {
    if (!canExport) {
      console.log('Reports: Unauthorized access (only strategy_team, executive, and superuser allowed) - redirecting');
      navigation.goBack();
    }
  }, [canExport, navigation]);

  // Manual pull-to-refresh — bypass the React Query cache and force a fresh
  // fetch. Year-driven refetches happen automatically via YearContext's
  // `['strategy']` invalidation.
  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await refetchComparison();
    } finally {
      setRefreshing(false);
    }
  }, [refetchComparison]);

  // Derived filtered + sorted organizations. Auto-applies whenever any filter
  // state changes, so picking from a dropdown updates the table/charts
  // immediately. The "Apply Filter" button just snaps back to page 1.
  const filteredOrgs = useMemo<OrganizationReport[]>(() => {
    let result = [...organizations];

    // Filter by organization (single-org pick)
    if (selectedOrg !== 'all') {
      result = result.filter((o) => o.id.toString() === selectedOrg);
    }

    // Sort by criteria — default ranking is by vision_achievement (desc)
    if (selectedCriteria === 'kpis') {
      result.sort((a, b) => (b.total_kpis || 0) - (a.total_kpis || 0));
    } else if (selectedCriteria === 'commitment') {
      result.sort((a, b) => (b.commitment_index || 0) - (a.commitment_index || 0));
    }

    // Re-rank after filter/sort so rank cells match the visible order
    return result.map((org, idx) => ({ ...org, rank: idx + 1 }));
  }, [organizations, selectedOrg, selectedCriteria]);

  // Reset page when any filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedOrg, selectedCriteria]);

  const handleApplyFilter = () => {
    setCurrentPage(1);
  };

  // Calculate pagination from filtered list
  const totalPages = Math.max(1, Math.ceil(filteredOrgs.length / itemsPerPage));
  const paginatedOrgs = filteredOrgs.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  if (!canExport) {
    return null;
  }

  // No more blocking ActivityIndicator on every cold visit. The query has
  // `placeholderData: keepPreviousData`, so revisits show cached rows
  // immediately. On the very first visit the page below renders an empty
  // shell while data loads — far better UX than a full-screen spinner.

  return (
    <PageContainer>
      <Navbar />
      <Breadcrumb
        items={[
          { label: t('navigation.home') },
          { label: t('navigation.reports') },
        ]}
      />
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={[styles.scrollContent, isMobile && styles.scrollContentMobile]}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={[COLORS.primary]} />
        }
      >
        {/* Page Title */}
        <Text style={[styles.pageTitle, isMobile && { fontSize: tokens.typography.paragraph["paragraph subheading font size"] }]}>{t('reports.mainDashboard')}</Text>

        {/* Stats Cards Row 1 - Counts (with icons) */}
        <View style={[styles.statsCardsRow, isMobile && styles.statsCardsRowMobile]}>
          <View style={[styles.statsCard, themedCardStyles.card]}>
            <View style={styles.statsCardHeader}>
              <Text style={styles.statsCardLabel}>{t('dashboard.strategicKPIs')}</Text>
              <View style={styles.statsCardIconBox}>
                <img
                  src={require('../../../assets/Icon-svg/chart-column-big.svg')}
                  alt=""
                  style={{ width: 24, height: 24 }}
                />
              </View>
            </View>
            <Text style={styles.statsCardValue}>
              {organizations.reduce((sum, org) => sum + (org.total_kpis || 0), 0)}
            </Text>
          </View>
          <View style={[styles.statsCard, themedCardStyles.card]}>
            <View style={styles.statsCardHeader}>
              <Text style={styles.statsCardLabel}>{t('reports.strategicInitiatives')}</Text>
              <View style={styles.statsCardIconBox}>
                <img
                  src={require('../../../assets/Icon-svg/rocket.svg')}
                  alt=""
                  style={{ width: 24, height: 24 }}
                />
              </View>
            </View>
            <Text style={styles.statsCardValue}>
              {organizations.reduce((sum, org) => sum + (org.total_initiatives || 0), 0)}
            </Text>
          </View>
          <View style={[styles.statsCard, themedCardStyles.card]}>
            <View style={styles.statsCardHeader}>
              <Text style={styles.statsCardLabel}>{t('reports.strategicGoals')}</Text>
              <View style={styles.statsCardIconBox}>
                <img
                  src={require('../../../assets/Icon-svg/goal.svg')}
                  alt=""
                  style={{ width: 24, height: 24 }}
                />
              </View>
            </View>
            <Text style={styles.statsCardValue}>
              {organizations.reduce((sum, org) => sum + (org.total_goals || 0), 0)}
            </Text>
          </View>
          <View style={[styles.statsCard, themedCardStyles.card]}>
            <View style={styles.statsCardHeader}>
              <Text style={styles.statsCardLabel}>{t('reports.organizations')}</Text>
              <View style={styles.statsCardIconBox}>
                <img
                  src={require('../../../assets/Icon-svg/building-2.svg')}
                  alt=""
                  style={{ width: 24, height: 24 }}
                />
              </View>
            </View>
            <Text style={styles.statsCardValue}>{organizations.length}</Text>
          </View>
        </View>

        {/* Stats Cards Row 2 - Performance (no icons, just labels) */}
        <View style={[styles.statsCardsRow, isMobile && styles.statsCardsRowMobile]}>
          <View style={[styles.statsCard, themedCardStyles.card]}>
            <View style={styles.statsCardHeaderSimple}>
              <Text style={styles.statsCardLabel}>{t('reports.orgsNeedImprovement')}</Text>
            </View>
            <Text style={[styles.statsCardValue, { color: '#D62728' }]}>
              {organizations.filter(o => o.rating === 'poor').length}
            </Text>
          </View>
          <View style={[styles.statsCard, themedCardStyles.card]}>
            <View style={styles.statsCardHeaderSimple}>
              <Text style={styles.statsCardLabel}>{t('reports.orgsAverage')}</Text>
            </View>
            <Text style={[styles.statsCardValue, { color: '#FF7F0E' }]}>
              {organizations.filter(o => o.rating === 'average').length}
            </Text>
          </View>
          <View style={[styles.statsCard, themedCardStyles.card]}>
            <View style={styles.statsCardHeaderSimple}>
              <Text style={styles.statsCardLabel}>{t('reports.orgsExcellent')}</Text>
            </View>
            <Text style={[styles.statsCardValue, { color: '#2CA02C' }]}>
              {organizations.filter(o => o.rating === 'excellent').length}
            </Text>
          </View>
          <View style={[styles.statsCard, themedCardStyles.card]}>
            <View style={styles.statsCardHeaderSimple}>
              <Text style={styles.statsCardLabel}>{t('reports.averageOverallPerformance')}</Text>
            </View>
            <Text style={[styles.statsCardValue, { color: '#FF7F0E' }]}>
              %{organizations.length > 0
                ? formatPct(organizations.reduce((sum, org) => sum + org.vision_achievement, 0) / organizations.length)
                : '0.00'}
            </Text>
          </View>
        </View>

        {/* Tab Content Container */}
        <View style={[styles.tabContent, themedCardStyles.card, isMobile && styles.tabContentMobile]}>
          {/* Tabs - aligned to right */}
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={[styles.tabsContainer, isMobile && styles.tabsContainerMobile]}
          >
            <TabButton
              label={t('reports.rankingAndRating')}
              isActive={activeTab === 'ranking'}
              onPress={() => setActiveTab('ranking')}
            />
            <TabButton
              label={t('reports.charts')}
              isActive={activeTab === 'charts'}
              onPress={() => setActiveTab('charts')}
            />
            <TabButton
              label={t('reports.detailedComparison')}
              isActive={activeTab === 'comparison'}
              onPress={() => setActiveTab('comparison')}
            />
            <TabButton
              label={t('reports.overview')}
              isActive={activeTab === 'overview'}
              onPress={() => setActiveTab('overview')}
            />
          </ScrollView>

          {/* Overview Tab Content */}
          {activeTab === 'overview' && (
            <>
              {/* Section Title */}
              <Text style={styles.sectionTitle}>{t('reports.orgsPerformanceOverview')}</Text>

            {/* Filters Row */}
            <View style={[styles.filtersRow, isMobile && styles.filtersRowMobile]}>
              {/* Filter Button - Left side */}
              <TouchableOpacity style={styles.applyFilterButton} onPress={handleApplyFilter}>
                <Text style={styles.applyFilterText}>{t('reports.applyFilter')}</Text>
              </TouchableOpacity>

              {/* Filters - Right side */}
              {Platform.OS === 'web' && (
                <View style={styles.filtersGroup}>
                  <FilterDropdown
                    label={t('reports.criteria')}
                    value={selectedCriteria}
                    options={[
                      { value: 'all', label: t('reports.allCriteria') },
                      { value: 'commitment', label: t('organizations.commitmentIndexLabel') },
                      { value: 'kpis', label: t('kpis.title') },
                    ]}
                    onChange={setSelectedCriteria}
                  />
                  <FilterDropdown
                    label={t('reports.organization')}
                    value={selectedOrg}
                    options={[
                      { value: 'all', label: t('reports.allOrganizations') },
                      ...organizations.map(org => ({ value: org.id.toString(), label: translateOrg(org.name) })),
                    ]}
                    onChange={setSelectedOrg}
                  />
                </View>
              )}
            </View>

            {/* Table - RTL direction */}
            <ScrollView
              horizontal={isMobile}
              showsHorizontalScrollIndicator={isMobile}
              contentContainerStyle={{ flexGrow: 1 }}
              style={{ width: '100%' }}
            >
              <View style={[styles.tableContainer, { width: '100%' }, isMobile && { minWidth: 900 }]}>
                {/* Table Header - RTL order: الترتيب | الجهة | عدد المؤشرات | جيد | متوسط | ضعيف | نسبة الإنجاز | المبادرات المكتملة | التقييم | view */}
                <View style={styles.tableHeader}>
                  <View style={[styles.tableHeaderCell, styles.rankCell]}>
                    <Text style={styles.tableHeaderText}>{t('reports.rank')}</Text>
                  </View>
                  <View style={[styles.tableHeaderCell, styles.nameCell]}>
                    <Text style={[styles.tableHeaderText, styles.tableHeaderTextRight]}>{t('reports.organization')}</Text>
                  </View>
                  <View style={[styles.tableHeaderCell, styles.kpiCountCell]}>
                    <Text style={styles.tableHeaderText}>{t('reports.kpiCount')}</Text>
                  </View>
                  <View style={[styles.tableHeaderCell, styles.kpiStatusCell]}>
                    <Text style={styles.tableHeaderText}>{t('reports.good')}</Text>
                  </View>
                  <View style={[styles.tableHeaderCell, styles.kpiStatusCell]}>
                    <Text style={styles.tableHeaderText}>{t('reports.average')}</Text>
                  </View>
                  <View style={[styles.tableHeaderCell, styles.kpiStatusCell]}>
                    <Text style={styles.tableHeaderText}>{t('reports.poor')}</Text>
                  </View>
                  <View style={[styles.tableHeaderCell, styles.achievementCell]}>
                    <Text style={styles.tableHeaderText}>{t('organizations.commitmentIndexLabel')}</Text>
                  </View>
                  <View style={[styles.tableHeaderCell, styles.initiativesCell]}>
                    <Text style={styles.tableHeaderText}>{t('reports.completedInitiatives')}</Text>
                  </View>
                  <View style={[styles.tableHeaderCell, styles.ratingCell]}>
                    <Text style={styles.tableHeaderText}>{t('reports.rating')}</Text>
                  </View>
                  <View style={[styles.tableHeaderCell, styles.viewCell]}>
                    <Text style={styles.tableHeaderText}></Text>
                  </View>
                </View>

                {/* Table Body */}
                {paginatedOrgs.map((org) => (
                  <TableRow
                    key={org.id}
                    org={org}
                    onPress={() => (navigation as any).navigate('OrganizationDetail', { id: org.id })}
                    translateOrg={translateOrg}
                    t={t}
                  />
                ))}
              </View>
            </ScrollView>

            {/* Pagination */}
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={filteredOrgs.length}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              t={t}
            />
            </>
          )}

          {/* Other tabs - placeholder content */}
          {activeTab === 'comparison' && (
            <View style={styles.comparisonTabContent}>
            {/* Organizations Grid - responsive columns */}
            <View style={[styles.comparisonGrid, isMobile && styles.comparisonGridMobile]}>
              {filteredOrgs.map((org) => (
                <ComparisonCard
                  key={org.id}
                  org={org}
                  onPress={() => (navigation as any).navigate('OrganizationDetail', { id: org.id })}
                  translateOrg={translateOrg}
                  t={t}
                />
              ))}
              </View>
            </View>
          )}

          {activeTab === 'charts' && (
            <View style={[styles.chartsTabContent, themedCardStyles.card, isMobile && styles.chartsTabContentMobile]}>
            {/* First Chart Section - توزيع حالة مؤشرات أداء الجهات */}
            <Text style={styles.chartSectionTitle}>{t('reports.kpiStatusDistribution')}</Text>

            {/* Filters Row */}
            <View style={[styles.chartFiltersRow, isMobile && styles.chartFiltersRowMobile]}>
              {Platform.OS === 'web' && (
                <>
                  <View style={styles.chartDateFilter}>
                    <Text style={styles.chartFilterLabel}>{t('reports.toDate')}</Text>
                    <View style={styles.chartDateInput}>
                      <input
                        type="date"
                        value={formatDateForInput(toDate)}
                        onChange={(e) => setToDate(new Date(e.target.value))}
                        style={{
                          border: 'none',
                          backgroundColor: 'transparent',
                          fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
                          color: COLORS.neutral700,
                          fontFamily: 'JF-Flat-Regular',
                          textAlign: 'center',
                          outline: 'none',
                          width: '100%',
                          cursor: 'pointer',
                        }}
                      />
                    </View>
                  </View>
                  <View style={styles.chartDateFilter}>
                    <Text style={styles.chartFilterLabel}>{t('reports.fromDate')}</Text>
                    <View style={styles.chartDateInput}>
                      <input
                        type="date"
                        value={formatDateForInput(fromDate)}
                        onChange={(e) => setFromDate(new Date(e.target.value))}
                        style={{
                          border: 'none',
                          backgroundColor: 'transparent',
                          fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
                          color: COLORS.neutral700,
                          fontFamily: 'JF-Flat-Regular',
                          textAlign: 'center',
                          outline: 'none',
                          width: '100%',
                          cursor: 'pointer',
                        }}
                      />
                    </View>
                  </View>
                  <View style={styles.chartOrgFilter}>
                    <Text style={styles.chartFilterLabel}>{t('reports.organizations')}</Text>
                    <FilterDropdown
                      label={t('reports.organizations')}
                      value={selectedOrg}
                      options={[
                        { value: 'all', label: t('reports.allOrganizations') },
                        ...organizations.map(org => ({ value: org.id.toString(), label: translateOrg(org.name) })),
                      ]}
                      onChange={setSelectedOrg}
                    />
                  </View>
                </>
              )}
            </View>

            {/* Stacked Bar Chart */}
            <StackedBarChart data={filteredOrgs} height={isMobile ? 300 : 350} translateOrg={translateOrg} t={t} />

            {/* Divider */}
            <View style={styles.chartDivider} />

            {/* Second Chart Section - مقارنة أداء الجهات
                Reuses the dashboard's OrganizationsComparisonSection — title,
                date/org filters, sort buttons, tooltip and chart card all come
                from that component, so there is nothing else to render here. */}
            <OrganizationsComparisonSection
              data={organizations}
              loading={loading}
              sortBy={comparisonSortBy}
              order={comparisonOrder}
              onSortChange={handleComparisonSortChange}
            />

            {/* Divider */}
            <View style={styles.chartDivider} />

            {/* Third Section - المقارنة الشاملة متعددة الأبعاد */}
            <Text style={styles.chartSectionTitle}>{t('reports.comprehensiveMultiDimensional')}</Text>

            {/* Radar Charts Grid */}
            <View style={[styles.radarCardsGrid, isMobile && styles.radarCardsGridMobile]}>
              {filteredOrgs.map((org) => (
                <OrganizationRadarCard
                  key={org.id}
                  org={org}
                  onPress={() => (navigation as any).navigate('OrganizationDetail', { id: org.id })}
                  translateOrg={translateOrg}
                  t={t}
                />
              ))}
              </View>
            </View>
          )}

          {activeTab === 'ranking' && (
            <View style={styles.rankingTabContent}>
            {/* Two Column Layout - RTL: Evaluation on left, Ranking on right */}
            <View style={[styles.rankingColumnsContainer, isMobile && styles.rankingColumnsContainerMobile]}>
              {/* Left Column - التقييم النهائي (Final Evaluation) */}
              <View style={styles.rankingEvaluationColumn}>
                <View style={[styles.rankingCard, themedCardStyles.card]}>
                  <Text style={styles.rankingCardTitle}>{t('reports.finalEvaluation')}</Text>

                  {/* Rating Distribution Table */}
                  <View style={styles.evaluationTable}>
                    {/* Table Header */}
                    <View style={styles.evaluationTableHeader}>
                      <View style={[styles.evaluationTableCell, styles.evaluationPercentCell]}>
                        <Text style={styles.evaluationHeaderText}>{t('reports.percentage')}</Text>
                      </View>
                      <View style={[styles.evaluationTableCell, styles.evaluationCountCell]}>
                        <Text style={styles.evaluationHeaderText}>{t('reports.orgCount')}</Text>
                      </View>
                      <View style={[styles.evaluationTableCell, styles.evaluationCategoryCell]}>
                        <Text style={styles.evaluationHeaderText}>{t('reports.category')}</Text>
                      </View>
                    </View>

                    {/* Excellent Row */}
                    <View style={styles.evaluationTableRow}>
                      <View style={[styles.evaluationTableCell, styles.evaluationPercentCell]}>
                        <Text style={styles.evaluationCellText}>
                          %{organizations.length > 0
                            ? formatPct((organizations.filter(o => o.rating === 'excellent').length / organizations.length) * 100)
                            : '0.00'}
                        </Text>
                      </View>
                      <View style={[styles.evaluationTableCell, styles.evaluationCountCell]}>
                        <Text style={styles.evaluationCellText}>
                          {organizations.filter(o => o.rating === 'excellent').length}
                        </Text>
                      </View>
                      <View style={[styles.evaluationTableCell, styles.evaluationCategoryCell]}>
                        <View style={[styles.evaluationCategoryBadge, { backgroundColor: '#DCFCE7' }]}>
                          <Text style={[styles.evaluationCategoryText, { color: tokens.colors.raw.green["500"] }]}>{t('charts.excellent')}</Text>
                        </View>
                      </View>
                    </View>

                    {/* Average Row */}
                    <View style={styles.evaluationTableRow}>
                      <View style={[styles.evaluationTableCell, styles.evaluationPercentCell]}>
                        <Text style={styles.evaluationCellText}>
                          %{organizations.length > 0
                            ? formatPct((organizations.filter(o => o.rating === 'average').length / organizations.length) * 100)
                            : '0.00'}
                        </Text>
                      </View>
                      <View style={[styles.evaluationTableCell, styles.evaluationCountCell]}>
                        <Text style={styles.evaluationCellText}>
                          {organizations.filter(o => o.rating === 'average').length}
                        </Text>
                      </View>
                      <View style={[styles.evaluationTableCell, styles.evaluationCategoryCell]}>
                        <View style={[styles.evaluationCategoryBadge, { backgroundColor: tokens.colors.raw.amber["100"] }]}>
                          <Text style={[styles.evaluationCategoryText, { color: '#F59E0B' }]}>{t('reports.average')}</Text>
                        </View>
                      </View>
                    </View>

                    {/* Poor Row */}
                    <View style={styles.evaluationTableRow}>
                      <View style={[styles.evaluationTableCell, styles.evaluationPercentCell]}>
                        <Text style={styles.evaluationCellText}>
                          %{organizations.length > 0
                            ? formatPct((organizations.filter(o => o.rating === 'poor').length / organizations.length) * 100)
                            : '0.00'}
                        </Text>
                      </View>
                      <View style={[styles.evaluationTableCell, styles.evaluationCountCell]}>
                        <Text style={styles.evaluationCellText}>
                          {organizations.filter(o => o.rating === 'poor').length}
                        </Text>
                      </View>
                      <View style={[styles.evaluationTableCell, styles.evaluationCategoryCell]}>
                        <View style={[styles.evaluationCategoryBadge, { backgroundColor: '#FEE2E2' }]}>
                          <Text style={[styles.evaluationCategoryText, { color: tokens.colors.raw.red["500"] }]}>{t('reports.poor')}</Text>
                        </View>
                      </View>
                    </View>
                  </View>

                  {/* Recommendations Section */}
                  <View style={styles.recommendationsSection}>
                    <Text style={styles.recommendationsTitle}>{t('reports.recommendations')}</Text>
                    <View style={styles.recommendationsList}>
                      {organizations.filter(o => o.rating === 'poor').length > 0 && (
                        <View style={styles.recommendationItem}>
                          <View style={styles.recommendationBullet} />
                          <Text style={styles.recommendationText}>
                            {t('reports.recommendationPoor', { count: organizations.filter(o => o.rating === 'poor').length })}
                          </Text>
                        </View>
                      )}
                      {organizations.filter(o => o.rating === 'average').length > 0 && (
                        <View style={styles.recommendationItem}>
                          <View style={styles.recommendationBullet} />
                          <Text style={styles.recommendationText}>
                            {t('reports.recommendationAverage')}
                          </Text>
                        </View>
                      )}
                      {organizations.filter(o => o.rating === 'excellent').length > 0 && (
                        <View style={styles.recommendationItem}>
                          <View style={styles.recommendationBullet} />
                          <Text style={styles.recommendationText}>
                            {t('reports.recommendationExcellent')}
                          </Text>
                        </View>
                      )}
                      <View style={styles.recommendationItem}>
                        <View style={styles.recommendationBullet} />
                        <Text style={styles.recommendationText}>
                          {t('reports.recommendationCoordination')}
                        </Text>
                      </View>
                    </View>
                  </View>
                </View>
              </View>

              {/* Right Column - ترتيب الجهات حسب الأداء (Organization Ranking) */}
              <View style={styles.rankingListColumn}>
                <View style={[styles.rankingCard, themedCardStyles.card]}>
                  <Text style={styles.rankingCardTitle}>{t('reports.orgRankingByPerformance')}</Text>

                  {/* Ranking List */}
                  <View style={styles.rankingList}>
                    {filteredOrgs.map((org, index) => (
                      <TouchableOpacity
                        key={org.id}
                        style={[
                          styles.rankingListItem,
                          index === 0 && styles.rankingListItemFirst,
                        ]}
                        onPress={() => (navigation as any).navigate('OrganizationDetail', { id: org.id })}
                      >
                        <View style={styles.rankingItemContent}>
                          <Text style={[
                            styles.rankingItemPercentage,
                            index === 0 && styles.rankingItemPercentageFirst,
                          ]}>
                            %{formatPct(org.vision_achievement)}
                          </Text>
                          <Text style={[
                            styles.rankingItemName,
                            index === 0 && styles.rankingItemNameFirst,
                          ]} numberOfLines={1}>
                            {translateOrg(org.name)}
                          </Text>
                        </View>
                        <View style={[
                          styles.rankingItemNumber,
                          index === 0 && styles.rankingItemNumberFirst,
                        ]}>
                          <Text style={[
                            styles.rankingItemNumberText,
                            index === 0 && styles.rankingItemNumberTextFirst,
                          ]}>
                            {index + 1}
                          </Text>
                        </View>
                      </TouchableOpacity>
                    ))}
                  </View>
                </View>
                </View>
              </View>
            </View>
          )}
        </View>
      </ScrollView>
    </PageContainer>
  );
}

const getStyles = (isRTL: boolean) => StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: tokens.spacing.absolute["6"],
    paddingBottom: tokens.spacing.absolute["10"],
  },
  scrollContentMobile: {
    padding: tokens.spacing.absolute["3"],
    paddingBottom: 30,
  },
  pageTitle: {
    fontSize: tokens.typography["heading 4"]["font size"],
    fontWeight: '600',
    color: COLORS.neutral900,
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["5"],
    fontFamily: 'JF-Flat-Medium',
  },
  statsCardsRow: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["5"],
    marginBottom: tokens.spacing.absolute["5"],
  },
  statsCardsRowMobile: {
    flexWrap: 'wrap',
    gap: tokens.spacing.absolute["3"],
  },
  statsCard: {
    flex: 1,
    minWidth: 150,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    padding: tokens.spacing.absolute["3"],
    // @ts-ignore - boxShadow is valid for web
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  statsCardHeader: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
    marginBottom: tokens.spacing.absolute["4"],
  },
  statsCardHeaderSimple: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    minHeight: 40,
    marginBottom: tokens.spacing.absolute["4"],
  },
  statsCardLabel: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.semantic.general.foreground,
    fontFamily: 'JF-Flat-Regular',
    textAlign: getTextAlign(isRTL),
  },
  statsCardIconBox: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  statsCardValue: {
    fontSize: 30,
    fontWeight: '700',
    color: tokens.colors.semantic.general.foreground,
    textAlign: 'center',
    fontFamily: FONTS.numbers,
  } as any,
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: tokens.spacing.absolute["4"],
    fontSize: tokens.typography.paragraph["paragraph font size"],
    color: COLORS.neutral600,
    fontFamily: 'JF-Flat-Regular',
  },
  tabsContainer: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["2"],
    marginBottom: tokens.spacing.absolute["6"],
    justifyContent: 'flex-end',
    paddingBottom: tokens.spacing.absolute["3"],
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: tokens.colors.raw.slate["200"],
    flexGrow: 1,
  },
  tabsContainerMobile: {
    paddingHorizontal: tokens.spacing.absolute["2"],
  },
  tabButton: {
    paddingVertical: tokens.spacing.absolute["2"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    backgroundColor: 'transparent',
    borderRadius: tokens.radii.absolute["radius-8"],
    minHeight: 36,
    justifyContent: 'center',
    alignItems: 'center',
  },
  tabButtonActive: {
    backgroundColor: '#A6813F',
  },
  tabButtonText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.raw.slate["700"],
    fontFamily: FONTS.medium,
    fontWeight: '700',
  },
  tabButtonTextActive: {
    color: tokens.colors.raw.white,
  },
  tabContent: {
    borderRadius: tokens.radii.absolute["radius-12"],
    padding: tokens.spacing.absolute["6"],
  },
  tabContentMobile: {
    padding: tokens.spacing.absolute["3"],
    borderRadius: tokens.radii.absolute["radius-8"],
  },
  sectionTitle: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontWeight: '600',
    color: COLORS.neutral900,
    marginBottom: tokens.spacing.absolute["4"],
    textAlign: getTextAlign(isRTL),
    fontFamily: 'JF-Flat-Medium',
  },
  filtersRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: tokens.spacing.absolute["6"],
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: tokens.spacing.absolute["3"],
  },
  filtersRowMobile: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  filtersGroup: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["3"],
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  filterContainer: {
    position: 'relative',
    minWidth: 140,
  },
  filterSelect: {
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.gray["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingVertical: tokens.spacing.absolute["2_5"],
    paddingHorizontal: 36,
    paddingEnd: tokens.spacing.absolute["3"],
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: COLORS.neutral700,
    fontFamily: 'JF-Flat-Regular',
    textAlign: getTextAlign(isRTL),
    width: '100%',
  } as any,
  filterArrow: {
    position: 'absolute',
    start: 12,
    top: '50%',
    transform: [{ translateY: -8 }],
  },
  applyFilterButton: {
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.gray["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingVertical: tokens.spacing.absolute["2_5"],
    paddingHorizontal: tokens.spacing.absolute["4"],
  },
  applyFilterText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: COLORS.neutral700,
    fontFamily: 'JF-Flat-Regular',
  },
  tableContainer: {
    width: '100%',
    overflow: 'hidden',
  },
  tableHeader: {
    flexDirection: 'row-reverse',
    backgroundColor: 'transparent',
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: tokens.colors.raw.gray["200"],
    paddingVertical: tokens.spacing.absolute["1"],
  },
  tableHeaderCell: {
    paddingVertical: tokens.spacing.absolute["4"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'row',
  },
  tableHeaderText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontWeight: '500',
    color: tokens.colors.raw.gray["500"],
    textAlign: 'center',
    fontFamily: 'JF-Flat-Medium',
    whiteSpace: 'nowrap',
  } as any,
  tableHeaderTextRight: {
    textAlign: getTextAlign(isRTL),
  },
  tableRow: {
    flexDirection: 'row-reverse',
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: tokens.colors.raw.gray["200"],
  },
  tableCell: {
    paddingVertical: tokens.spacing.absolute["4"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'row',
  },
  tableCellText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: COLORS.neutral700,
    textAlign: 'center',
    fontFamily: FONTS.numbers,
  },
  // Column widths - RTL order: الترتيب | الجهة | عدد المؤشرات | جيد | متوسط | ضعيف | نسبة الإنجاز | المبادرات المكتملة | التقييم | view
  rankCell: {
    width: 70,
    alignItems: 'center',
  },
  nameCell: {
    flex: 1,
    minWidth: 200,
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
  },
  kpiCountCell: {
    width: 120,
    alignItems: 'center',
  },
  kpiStatusCell: {
    width: 70,
    alignItems: 'center',
  },
  achievementCell: {
    width: 120,
    alignItems: 'center',
  },
  initiativesCell: {
    width: 140,
    alignItems: 'center',
  },
  ratingCell: {
    width: 90,
    alignItems: 'center',
  },
  viewCell: {
    width: 50,
    alignItems: 'center',
  },
  // Cell content styles
  orgName: {
    fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
    color: COLORS.neutral900,
    textAlign: getTextAlign(isRTL),
    fontFamily: 'JF-Flat-Regular',
  },
  rankText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontWeight: '600',
    color: COLORS.neutral900,
    fontFamily: FONTS.numbers,
  },
  viewButton: {
    padding: tokens.spacing.absolute["1"],
  },
  ratingBadge: {
    paddingVertical: tokens.spacing.absolute["1"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    borderRadius: tokens.radii.absolute["radius-4"],
    borderWidth: tokens.spacing.absolute["px"],
    backgroundColor: 'transparent',
  },
  ratingText: {
    fontSize: tokens.typography.paragraph["paragraph caption font size"],
    fontWeight: '500',
    fontFamily: 'JF-Flat-Medium',
  },
  achievementCellContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  achievementText: {
    fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
    color: COLORS.neutral700,
    fontFamily: FONTS.numbers,
  },
  paginationRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: tokens.spacing.absolute["3"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    borderTopWidth: tokens.spacing.absolute["px"],
    borderTopColor: tokens.colors.raw.slate["200"],
    marginTop: tokens.spacing.absolute["4"],
  },
  paginationControls: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  paginationButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["1_5"],
    gap: tokens.spacing.absolute["1"],
  },
  paginationButtonDisabled: {
    opacity: 0.4,
  },
  paginationButtonText: {
    fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
    color: COLORS.neutral600,
    fontFamily: FONTS.regular,
  },
  paginationNumbers: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["1"],
  },
  paginationNumber: {
    width: 32,
    height: 32,
    borderRadius: tokens.radii.absolute["radius-6"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    backgroundColor: tokens.colors.raw.white,
    justifyContent: 'center',
    alignItems: 'center',
  },
  paginationNumberActive: {
    borderColor: tokens.colors.raw.slate["700"],
  },
  paginationNumberText: {
    fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
    color: COLORS.neutral600,
    fontFamily: FONTS.numbers,
  },
  paginationNumberTextActive: {
    color: COLORS.neutral900,
    fontFamily: FONTS.medium,
  },
  paginationInfoText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: COLORS.neutral500,
    fontFamily: FONTS.regular,
  },
  comingSoonContainer: {
    alignItems: 'center',
    paddingVertical: 60,
  },
  comingSoonTitle: {
    fontSize: tokens.typography.paragraph["paragraph subheading font size"],
    fontWeight: '600',
    color: COLORS.neutral900,
    marginTop: tokens.spacing.absolute["4"],
    marginBottom: tokens.spacing.absolute["2"],
    fontFamily: 'JF-Flat-Medium',
  },
  comingSoonText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: COLORS.neutral500,
    fontFamily: 'JF-Flat-Regular',
  },

  // Comparison Tab Styles (مقارنة تفصيلية)
  comparisonTabContent: {
    padding: tokens.spacing.absolute["0"],
  },
  comparisonGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: tokens.spacing.absolute["4"],
    justifyContent: 'flex-end',
  },
  comparisonGridMobile: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  comparisonCard: {
    width: '31.5%',
    minWidth: 280,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    overflow: 'hidden',
  },
  comparisonCardHeader: {
    backgroundColor: tokens.colors.raw.gray["50"],
    paddingVertical: tokens.spacing.absolute["3"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: tokens.colors.raw.gray["200"],
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  comparisonCardTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontWeight: '600',
    color: COLORS.neutral900,
    textAlign: getTextAlign(isRTL),
    fontFamily: 'JF-Flat-Medium',
    flex: 1,
  },
  comparisonRatingBadge: {
    paddingVertical: tokens.spacing.absolute["1"],
    paddingHorizontal: tokens.spacing.absolute["2_5"],
    borderRadius: tokens.radii.absolute["radius-4"],
    borderWidth: tokens.spacing.absolute["px"],
    backgroundColor: 'transparent',
    marginStart: tokens.spacing.absolute["2"],
  },
  comparisonRatingText: {
    fontSize: tokens.typography.paragraph["paragraph caption font size"],
    fontWeight: '500',
    fontFamily: 'JF-Flat-Medium',
  },
  comparisonStatsGrid: {
    padding: tokens.spacing.absolute["3"],
    gap: tokens.spacing.absolute["2"],
  },
  comparisonProgressSection: {
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2"],
  },
  comparisonProgressHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: tokens.spacing.absolute["1_5"],
  },
  comparisonProgressLabel: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: COLORS.neutral600,
    fontFamily: 'JF-Flat-Regular',
  },
  comparisonProgressValue: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontWeight: '600',
    fontFamily: FONTS.numbers,
  },
  comparisonProgressBarBg: {
    height: 8,
    backgroundColor: tokens.colors.raw.gray["200"],
    borderRadius: tokens.radii.absolute["radius-4"],
    overflow: 'hidden',
  },
  comparisonProgressBarFill: {
    height: '100%',
    borderRadius: tokens.radii.absolute["radius-4"],
  },
  comparisonStatsRow: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["2"],
  },
  comparisonStatBox: {
    flex: 1,
    paddingVertical: tokens.spacing.absolute["2"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    borderRadius: tokens.radii.absolute["radius-6"],
    alignItems: 'center',
  },
  statBoxDefault: {
    backgroundColor: tokens.colors.raw.gray["100"],
  },
  statBoxGreen: {
    backgroundColor: '#DCFCE7',
  },
  statBoxYellow: {
    backgroundColor: tokens.colors.raw.amber["100"],
  },
  statBoxRed: {
    backgroundColor: '#FEE2E2',
  },
  comparisonStatLabel: {
    fontSize: tokens.typography.paragraph["paragraph caption font size"],
    color: COLORS.neutral600,
    marginBottom: tokens.spacing.absolute["0_5"],
    fontFamily: 'JF-Flat-Regular',
  },
  comparisonStatValue: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontWeight: '600',
    color: COLORS.neutral900,
    fontFamily: 'JF-Flat-Medium',
  },
  comparisonBottomStats: {
    borderTopWidth: tokens.spacing.absolute["px"],
    borderTopColor: tokens.colors.raw.gray["200"],
    paddingVertical: tokens.spacing.absolute["2"],
    paddingHorizontal: tokens.spacing.absolute["3"],
  },
  comparisonBottomRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: tokens.spacing.absolute["1"],
  },
  comparisonBottomLabel: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: COLORS.neutral600,
    fontFamily: 'JF-Flat-Regular',
  },
  comparisonBottomValue: {
    fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
    fontWeight: '600',
    color: COLORS.neutral900,
    fontFamily: 'JF-Flat-Medium',
  },

  // Charts Tab Styles (رسومات بيانية)
  chartsTabContent: {
    borderRadius: tokens.radii.absolute["radius-12"],
    padding: tokens.spacing.absolute["5"],
    borderWidth: tokens.spacing.absolute["px"],
  },
  chartsTabContentMobile: {
    padding: tokens.spacing.absolute["3"],
    borderRadius: tokens.radii.absolute["radius-8"],
  },
  chartSectionTitle: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontWeight: '600',
    color: COLORS.neutral900,
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["4"],
    fontFamily: 'JF-Flat-Medium',
  },
  chartFiltersRow: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["4"],
    marginBottom: tokens.spacing.absolute["6"],
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
  },
  chartFiltersRowMobile: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: tokens.spacing.absolute["3"],
  },
  chartDateFilter: {
    gap: tokens.spacing.absolute["1_5"],
  },
  chartFilterLabel: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: COLORS.neutral600,
    textAlign: getTextAlign(isRTL),
    fontFamily: 'JF-Flat-Regular',
  },
  chartDateInput: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.gray["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingVertical: tokens.spacing.absolute["2"],
    paddingHorizontal: tokens.spacing.absolute["3"],
  },
  chartOrgFilter: {
    gap: tokens.spacing.absolute["1_5"],
    minWidth: 160,
  },
  chartContainer: {
    backgroundColor: '#FAFAFA',
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["4"],
    minWidth: 300,
    minHeight: 350,
  },
  chartDivider: {
    height: 1,
    backgroundColor: tokens.colors.raw.gray["200"],
    marginVertical: tokens.spacing.absolute["8"],
  },
  chartLegend: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: tokens.spacing.absolute["6"],
    marginTop: tokens.spacing.absolute["4"],
    paddingTop: tokens.spacing.absolute["4"],
    borderTopWidth: tokens.spacing.absolute["px"],
    borderTopColor: tokens.colors.raw.gray["200"],
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["1_5"],
  },
  legendDot: {
    width: 10,
    height: 10,
    borderRadius: tokens.radii.absolute["radius-5"],
  },
  legendText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: COLORS.neutral600,
    fontFamily: 'JF-Flat-Regular',
  },

  // Tooltip Styles
  tooltipContainer: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["3"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.gray["200"],
    // @ts-ignore - boxShadow is valid for web
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
    elevation: 3,
  },
  tooltipLabel: {
    fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
    fontWeight: '600',
    color: COLORS.neutral900,
    marginBottom: tokens.spacing.absolute["2"],
    textAlign: getTextAlign(isRTL),
    fontFamily: 'JF-Flat-Medium',
  },
  tooltipRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["1_5"],
    marginVertical: tokens.spacing.absolute["0_5"],
  },
  tooltipDot: {
    width: 8,
    height: 8,
    borderRadius: tokens.radii.absolute["radius-4"],
  },
  tooltipText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: COLORS.neutral700,
    fontFamily: 'JF-Flat-Regular',
  },

  // Radar Chart Styles (المقارنة الشاملة متعددة الأبعاد)
  // Vertical layout: Chart on top, Legend below in 2-column grid (matching Figma)
  radarCardsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: tokens.spacing.absolute["4"],
    justifyContent: 'flex-end',
  },
  radarCardsGridMobile: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  radarCard: {
    width: '23.5%',
    minWidth: 240,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    padding: tokens.spacing.absolute["3"],
    overflow: 'hidden',
  },
  radarCardHeader: {
    marginBottom: tokens.spacing.absolute["2"],
  },
  radarCardTitle: {
    fontSize: 10,
    color: COLORS.neutral700,
    textAlign: getTextAlign(isRTL),
    fontFamily: 'JF-Flat-Regular',
  },
  radarCardContent: {
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacing.absolute["3"],
  },
  radarChartWrapper: {
    width: '100%',
    minWidth: 200,
    minHeight: 140,
    alignItems: 'center',
  },
  radarLegendContainer: {
    width: '100%',
    gap: tokens.spacing.absolute["1_5"],
  },
  radarLegendRow: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    gap: tokens.spacing.absolute["2"],
  },
  radarLegendDot: {
    width: 12,
    height: 12,
    borderRadius: tokens.radii.absolute["radius-6"],
    flexShrink: 0,
  },
  radarLegendLabel: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: COLORS.neutral700,
    fontFamily: 'JF-Flat-Regular',
    flexShrink: 0,
  },
  radarLegendDash: {
    flex: 1,
    height: 0,
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderStyle: 'dashed',
    borderColor: '#94A3B8',
    minWidth: 8,
  },
  radarLegendValue: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: COLORS.neutral700,
    fontFamily: 'JF-Flat-Regular',
    flexShrink: 0,
  },
  radarTooltip: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-6"],
    padding: tokens.spacing.absolute["2"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.gray["200"],
    // @ts-ignore - boxShadow is valid for web
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
    elevation: 3,
  },
  radarTooltipLabel: {
    fontSize: tokens.typography.paragraph["paragraph caption font size"],
    color: COLORS.neutral600,
    fontFamily: 'JF-Flat-Regular',
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["0_5"],
  },
  radarTooltipValue: {
    fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
    fontWeight: '600',
    color: COLORS.neutral900,
    fontFamily: 'JF-Flat-Medium',
    textAlign: getTextAlign(isRTL),
  },

  // Ranking Tab Styles (الترتيب والتقييم)
  rankingTabContent: {
    padding: tokens.spacing.absolute["0"],
  },
  rankingColumnsContainer: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["5"],
    alignItems: 'flex-start',
  },
  rankingColumnsContainerMobile: {
    flexDirection: 'column-reverse',
    gap: tokens.spacing.absolute["4"],
  },
  rankingEvaluationColumn: {
    flex: 1,
    minWidth: 300,
  },
  rankingListColumn: {
    flex: 1,
    minWidth: 350,
  },
  rankingCard: {
    borderRadius: tokens.radii.absolute["radius-12"],
    borderWidth: tokens.spacing.absolute["px"],
    padding: tokens.spacing.absolute["5"],
  },
  rankingCardTitle: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontWeight: '600',
    color: COLORS.neutral900,
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["4"],
    fontFamily: 'JF-Flat-Medium',
  },

  // Evaluation Table Styles
  evaluationTable: {
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.gray["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    overflow: 'hidden',
  },
  evaluationTableHeader: {
    flexDirection: 'row',
    backgroundColor: tokens.colors.raw.gray["50"],
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: tokens.colors.raw.gray["200"],
  },
  evaluationTableRow: {
    flexDirection: 'row',
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: tokens.colors.raw.gray["200"],
  },
  evaluationTableCell: {
    paddingVertical: tokens.spacing.absolute["3"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    justifyContent: 'center',
    alignItems: 'center',
  },
  evaluationCategoryCell: {
    flex: 1,
    alignItems: 'flex-end',
  },
  evaluationCountCell: {
    width: 100,
  },
  evaluationPercentCell: {
    width: 80,
  },
  evaluationHeaderText: {
    fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
    fontWeight: '600',
    color: COLORS.neutral600,
    fontFamily: 'JF-Flat-Medium',
    textAlign: 'center',
  },
  evaluationCellText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: COLORS.neutral700,
    fontFamily: 'JF-Flat-Regular',
    textAlign: 'center',
  },
  evaluationCategoryBadge: {
    paddingVertical: tokens.spacing.absolute["1_5"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    borderRadius: tokens.radii.absolute["radius-6"],
  },
  evaluationCategoryText: {
    fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
    fontWeight: '600',
    fontFamily: 'JF-Flat-Medium',
  },

  // Recommendations Styles
  recommendationsSection: {
    marginTop: tokens.spacing.absolute["5"],
    paddingTop: tokens.spacing.absolute["4"],
    borderTopWidth: tokens.spacing.absolute["px"],
    borderTopColor: tokens.colors.raw.gray["200"],
  },
  recommendationsTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontWeight: '600',
    color: COLORS.neutral900,
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["3"],
    fontFamily: 'JF-Flat-Medium',
  },
  recommendationsList: {
    gap: tokens.spacing.absolute["2"],
  },
  recommendationItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: tokens.spacing.absolute["2"],
  },
  recommendationBullet: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: COLORS.primary,
    marginTop: tokens.spacing.absolute["1_5"],
  },
  recommendationText: {
    flex: 1,
    fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
    color: COLORS.neutral700,
    textAlign: getTextAlign(isRTL),
    lineHeight: 20,
    fontFamily: 'JF-Flat-Regular',
  },

  // Ranking List Styles
  rankingList: {
    gap: tokens.spacing.absolute["2"],
  },
  rankingListItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    paddingVertical: tokens.spacing.absolute["3"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    backgroundColor: tokens.colors.raw.gray["50"],
    borderRadius: tokens.radii.absolute["radius-8"],
    gap: tokens.spacing.absolute["3"],
  },
  rankingListItemFirst: {
    backgroundColor: tokens.colors.raw.amber["100"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: '#F59E0B',
  },
  rankingItemContent: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["3"],
  },
  rankingItemName: {
    flex: 1,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: COLORS.neutral700,
    textAlign: getTextAlign(isRTL),
    fontFamily: 'JF-Flat-Regular',
  },
  rankingItemNameFirst: {
    color: COLORS.neutral900,
    fontWeight: '600',
    fontFamily: 'JF-Flat-Medium',
  },
  rankingItemPercentage: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: COLORS.neutral600,
    fontFamily: 'JF-Flat-Regular',
  },
  rankingItemPercentageFirst: {
    color: '#B45309',
    fontWeight: '600',
    fontFamily: 'JF-Flat-Medium',
  },
  rankingItemNumber: {
    width: 32,
    height: 32,
    borderRadius: tokens.radii.absolute["radius-16"],
    backgroundColor: tokens.colors.raw.white,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.gray["200"],
  },
  rankingItemNumberFirst: {
    backgroundColor: '#F59E0B',
    borderColor: '#F59E0B',
  },
  rankingItemNumberText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontWeight: '600',
    color: COLORS.neutral700,
    fontFamily: 'JF-Flat-Medium',
  },
  rankingItemNumberTextFirst: {
    color: tokens.colors.raw.white,
  },
});

const styles = getStyles(true);
